import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FlexList,
  Inscription,
  Preloader,
  TransactionsNotFoundIcon,
  useResponsive,
} from '@common-fe/common-fe';

import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import useHighlightedPaymentMethodStore from '@/modules/user/components/ManageProfile/components/ReimbursementOptions/stores/useHighlightedPaymentMethod.store';

import EditPaymentMethodModal from '../PaymentMethodModal/EditPaymentMethodModal';
import RemovePaymentMethodModal from '../PaymentMethodModal/RemovePaymentMethodModal';

import usePaymentMethodOptions from './hooks/usePaymentMethodOptions';
import usePaymentMethodsList from './hooks/usePaymentMethodsList';
import MobilePaymentMethodCard from './MobilePaymentMethodCard';

interface Props {
  isFetching: boolean;
  refetchPaymentMethods: () => void;
}

const PaymentMethodsList: React.FC<Props> = ({ isFetching, refetchPaymentMethods }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  const { highlightedPaymentMethodId, highlightedPaymentMethodIds } = useHighlightedPaymentMethodStore();

  const paymentMethods = useStore((state) => state.methods);
  const { formattedList, headers, rawList } = usePaymentMethodsList(
    paymentMethods,
    highlightedPaymentMethodId,
    highlightedPaymentMethodIds,
    refetchPaymentMethods
  );
  const { options } = usePaymentMethodOptions({
    refetchPaymentMethods,
    handleOpenEditModal: () => setIsEditModalVisible(true),
    handleOpenRemoveModal: () => setIsRemoveModalVisible(true),
  });

  const placeholder = (
    <Box align="center">
      <TransactionsNotFoundIcon color="iconDisabled" />
      <Inscription margin={{ top: 'spacing12' }} size="large" weight={700}>
        {t('There are no reimbursement options yet')}
      </Inscription>
    </Box>
  );

  return (
    <>
      <Box margin={{ top: 'spacing16' }} data-testid="PaymentMethodsList_wrapper">
        {isFetching && !paymentMethods.length ? (
          <Box pad={{ bottom: 'spacing24' }} data-testid="PaymentMethodsList_loader">
            <Preloader />
          </Box>
        ) : isMobile ? (
          <Box key={rawList.length} data-testid="PaymentMethodsList_mobile">
            {rawList.length ? (
              rawList.map(({ paymentMethod, employeeFee, isBannerShown, isHighlighted }) => (
                <MobilePaymentMethodCard
                  key={paymentMethod?.id}
                  paymentMethod={paymentMethod}
                  options={options}
                  employeeFee={employeeFee}
                  isBannerShown={isBannerShown}
                  isHighlighted={isHighlighted}
                  refetchPaymentMethods={refetchPaymentMethods}
                />
              ))
            ) : (
              <Box margin={{ top: 'spacing24' }}>{placeholder}</Box>
            )}
          </Box>
        ) : (
          <FlexList
            headers={headers}
            rows={formattedList}
            total={formattedList.length}
            options={options}
            pad="0px"
            placeholder={placeholder}
          />
        )}
      </Box>
      {isEditModalVisible && (
        <EditPaymentMethodModal
          refetchPaymentMethods={refetchPaymentMethods}
          onSetVisible={setIsEditModalVisible}
        />
      )}
      {isRemoveModalVisible && (
        <RemovePaymentMethodModal
          refetchPaymentMethods={refetchPaymentMethods}
          onSetVisible={setIsRemoveModalVisible}
        />
      )}
    </>
  );
};

export default PaymentMethodsList;
